import axios from 'axios';
import { getCsrfToken, CSRF_HEADER_NAME } from '../utils/csrf';

/**
 * Consume an NDJSON keep-alive stream from a long-running endpoint.
 *
 * The server emits periodic {"event":"processing"} heartbeats (which keep an
 * idle proxy from returning a silent 504) followed by exactly one terminal line:
 *   - {"event":"result","data":<payload>}  -> resolves with <payload>
 *   - {"event":"error","detail":<message>} -> throws an axios-shaped error
 * so existing callers can keep reading error?.response?.data?.detail.
 */
async function consumeNdjsonStream(url: string, method: 'GET' | 'POST', body?: any): Promise<any> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') {
    const token = getCsrfToken();
    if (token) headers[CSRF_HEADER_NAME] = token;
  }

  const resp = await fetch(url, {
    method,
    credentials: 'include',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!resp.ok || !resp.body) {
    let detail = 'Request failed';
    try {
      const data = await resp.json();
      detail = data?.detail || detail;
    } catch {
      /* non-JSON error body */
    }
    const err: any = new Error(detail);
    err.response = { status: resp.status, data: { detail } };
    throw err;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: any = null;

  const handleLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const evt = JSON.parse(trimmed);
    if (evt.event === 'result') {
      result = evt.data;
    } else if (evt.event === 'error') {
      const err: any = new Error(evt.detail || 'Request failed');
      err.response = { data: { detail: evt.detail || 'Request failed' } };
      throw err;
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 1);
      handleLine(line);
    }
  }
  if (buffer.trim()) handleLine(buffer);

  return result;
}

async function axiosRequest<T>(axiosPromise: Promise<T>): Promise<T> {
  try {
    return await axiosPromise;
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      // Do NOT redirect on 401 here. PermissionsContext + RequireAuth handle
      // unauthenticated state globally without causing a full page reload loop.
      throw error;
    }
    throw error;
  }
}

export async function uploadRulebook(data: FormData): Promise<any> {
  try {
    const response = await axiosRequest(axios.post('/api/upload/Upload_rule_book', data, {
      withCredentials: true,
      headers: {
        'Content-Type': 'multipart/form-data', // Axios sets the boundary automatically
      },
      
    }));
    return response.data;
  } catch (error) {
    // Axios errors can be typed more specifically if desired
    if (axios.isAxiosError(error)) {
      throw error;
    }
    throw error;
  }
}

export async function analyzeRule(data: any): Promise<any> {
  // Long-running: consumes the NDJSON keep-alive stream and returns the final
  // result payload (or throws with error?.response?.data?.detail on failure).
  return consumeNdjsonStream('/api/upload/analyse_uploaded_rule', 'POST', data);
}

export async function getInstructions(): Promise<any> {
  try {
    const response = await axiosRequest(axios.get('/api/rules/get_manage_rules?category=instruction',  {
      withCredentials: true,
    }));
    return response.data;
  } catch (error) {
    // Axios errors can be typed more specifically if desired
    if (axios.isAxiosError(error)) {
      throw error;
    }
    throw error;
  }
}

export async function updateInstruction(data: any): Promise<any> {
  try {
    const response = await axiosRequest(axios.put('/api/rules/update_instruction', data, {
      withCredentials: true,
    }));
    return response.data;
  } catch (error) {
    // Axios errors can be typed more specifically if desired
    if (axios.isAxiosError(error)) {
      throw error;
    }
    throw error;
  }
}

export async function addInstruction(data: any): Promise<any> {
  try {
    const response = await axiosRequest(axios.post('/api/rules/add_instruction', data, {
      withCredentials: true,
    }));
    return response.data;
  } catch (error) {
    // Axios errors can be typed more specifically if desired
    if (axios.isAxiosError(error)) {
      throw error;
    }
    throw error;
  }
}

export async function getRules(): Promise<any> {
  try {
    const response = await axiosRequest(axios.get('/api/rules/get_manage_rules?category=rule',  {
      withCredentials: true,
    }));
    return response.data;
  } catch (error) {
    // Axios errors can be typed more specifically if desired
    if (axios.isAxiosError(error)) {
      throw error;
    }
    throw error;
  }
}

export async function updateRule(data: any): Promise<any> {
  try {
    const response = await axiosRequest(axios.put('/api/rules/update_rule', data, {
      withCredentials: true,
    }));
    return response.data;
  } catch (error) {
    // Axios errors can be typed more specifically if desired
    if (axios.isAxiosError(error)) {
      throw error;
    }
    throw error;
  }
}

export async function addRule(data: any): Promise<any> {
  try {
    const response = await axiosRequest(axios.post('/api/rules/add_rule', data, {
      withCredentials: true,
    }));
    return response.data;
  } catch (error) {
    // Axios errors can be typed more specifically if desired
    if (axios.isAxiosError(error)) {
      throw error;
    }
    throw error;
  }
}

export async function deleteInstruction(data: any): Promise<any> {
  try {
    const response = await axiosRequest(axios.delete('/api/rules/delete_manage_rule',  {
       data: data, 
      withCredentials: true
    }));
    return response.data;
  } catch (error) {
    // Axios errors can be typed more specifically if desired
    if (axios.isAxiosError(error)) {
      throw error;
    }
    throw error;
  }
}

export async function download_ManageRules(): Promise<any> {
  try {
    const response = await axiosRequest(axios.get('/api/rules/download_manage_rule',  {
      withCredentials: true,
      responseType: 'arraybuffer',
      headers: {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    }));
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    return blob;
  } catch (error) {
    // Axios errors can be typed more specifically if desired
    if (axios.isAxiosError(error)) {
      throw error;
    }
    throw error;
  }
}

export async function proceedManageRule(): Promise<any> {
  // Long-running: consumes the NDJSON keep-alive stream and returns the final
  // result payload (or throws with error?.response?.data?.detail on failure).
  return consumeNdjsonStream('/api/rules/proceed_manage_rule', 'GET');
}

export async function uploadPayrollFiles(formData: FormData): Promise<any> {
  try {
    const response = await axiosRequest(axios.post(
      "/api/data/Upload_payroll_files",
      formData,
      { withCredentials: true }
    ));
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw error;
    }
    throw error;
  }
}

export async function getNotification(notification_id: string): Promise<any> {
  const response = await axiosRequest(axios.get(`/api/data/get_notifications?notification_id=${notification_id}`, {
    withCredentials: true,
  }));
  return response.data;
}